import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faKey,
  faEye,
  faEyeSlash,
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faSave
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const TrocarSenhaObrigatoria = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, deveTrocarSenha, isLoading } = useAuth();
  
  const [formData, setFormData] = useState({
    novaSenha: '',
    confirmarSenha: ''
  });
  
  const [showPasswords, setShowPasswords] = useState({
    novaSenha: false,
    confirmarSenha: false
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Redirecionar se não estiver autenticado ou não precisar trocar senha
  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        navigate('/login');
      } else if (!deveTrocarSenha) {
        navigate('/dashboard');
      }
    }
  }, [isAuthenticated, deveTrocarSenha, isLoading, navigate]);
  
  // Validações de senha
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUpper: false,
    hasLower: false,
    hasNumber: false,
    passwordsMatch: false
  });

  useEffect(() => {
    validatePassword();
  }, [formData.novaSenha, formData.confirmarSenha]);

  const validatePassword = () => {
    const { novaSenha, confirmarSenha } = formData;
    
    setPasswordValidation({
      minLength: novaSenha.length >= 8,
      hasUpper: /[A-Z]/.test(novaSenha),
      hasLower: /[a-z]/.test(novaSenha),
      hasNumber: /[0-9]/.test(novaSenha),
      passwordsMatch: novaSenha === confirmarSenha && novaSenha.length > 0
    });
  };

  const isPasswordValid = () => {
    return Object.values(passwordValidation).every(value => value === true);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid()) {
      setError('Por favor, atenda a todos os requisitos da senha.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await api.put('/auth/force-change-password', {
        novaSenha: formData.novaSenha,
        confirmarSenha: formData.confirmarSenha
      });
      
      setSuccess(true);
      
      // Fazer logout após trocar a senha (backend invalida sessões)
      setTimeout(async () => {
        await logout();
        navigate('/login');
      }, 3000);
      
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  const ValidationItem = ({ valid, text }) => (
    <div className={`flex items-center gap-2 text-sm ${valid ? 'text-green-600' : 'text-gray-500'}`}>
      <FontAwesomeIcon 
        icon={valid ? faCheckCircle : faTimesCircle} 
        className={valid ? 'text-green-500' : 'text-gray-400'} 
      />
      <span>{text}</span>
    </div>
  );

  // Mostrar loading enquanto verifica autenticação
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-green-400 to-blue-500">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  // Não renderizar se não estiver autenticado ou não precisar trocar senha
  if (!isAuthenticated || !deveTrocarSenha) {
    return null;
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-green-400 to-blue-500 p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 text-3xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Senha Alterada com Sucesso!</h2>
          <p className="text-gray-600 mb-4">
            Sua senha foi alterada. Você será redirecionado para fazer login novamente.
          </p>
          <div className="animate-pulse text-gray-500 text-sm">
            Redirecionando...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-green-400 to-blue-500 p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FontAwesomeIcon icon={faKey} className="text-amber-600 text-3xl" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Troca de Senha Obrigatória
          </h1>
          <p className="text-gray-600">
            {user?.nome ? `Olá, ${user.nome.split(' ')[0]}!` : 'Olá!'} Sua senha foi resetada e você precisa criar uma nova senha para continuar.
          </p>
        </div>

        {/* Alerta */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500 mt-0.5" />
            <div className="text-sm text-amber-700">
              <strong>Atenção:</strong> Por motivos de segurança, você precisa criar uma nova senha antes de acessar o sistema.
            </div>
          </div>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Erro */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Nova Senha */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nova Senha
            </label>
            <div className="relative">
              <input
                type={showPasswords.novaSenha ? 'text' : 'password'}
                name="novaSenha"
                value={formData.novaSenha}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 pr-10"
                placeholder="Digite sua nova senha"
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('novaSenha')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                <FontAwesomeIcon icon={showPasswords.novaSenha ? faEyeSlash : faEye} />
              </button>
            </div>
          </div>

          {/* Confirmar Senha */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar Nova Senha
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirmarSenha ? 'text' : 'password'}
                name="confirmarSenha"
                value={formData.confirmarSenha}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 pr-10"
                placeholder="Confirme sua nova senha"
                required
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirmarSenha')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                <FontAwesomeIcon icon={showPasswords.confirmarSenha ? faEyeSlash : faEye} />
              </button>
            </div>
          </div>

          {/* Requisitos da Senha */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Requisitos da senha:</p>
            <div className="space-y-1">
              <ValidationItem valid={passwordValidation.minLength} text="Mínimo de 8 caracteres" />
              <ValidationItem valid={passwordValidation.hasUpper} text="Pelo menos uma letra maiúscula" />
              <ValidationItem valid={passwordValidation.hasLower} text="Pelo menos uma letra minúscula" />
              <ValidationItem valid={passwordValidation.hasNumber} text="Pelo menos um número" />
              <ValidationItem valid={passwordValidation.passwordsMatch} text="Senhas coincidem" />
            </div>
          </div>

          {/* Botão Submit */}
          <button
            type="submit"
            disabled={loading || !isPasswordValid()}
            className={`w-full py-3 px-4 rounded-lg font-medium text-white transition-colors flex items-center justify-center gap-2 ${
              isPasswordValid() && !loading
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Alterando senha...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Salvar Nova Senha
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default TrocarSenhaObrigatoria;
